import { HttpBackend, HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DateTime } from 'luxon';
import { Observable, catchError, concat, interval, map, of, shareReplay, switchMap, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';

import { isMarkdownV1 } from './announcement-markdown.service';
import { AnnouncementManifest, PortalAnnouncement } from './release-notification.model';

const ANNOUNCEMENT_URL = '/announcement.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const JSON_CONTENT_TYPE = /^application\/json(?:\s*;|$)/i;
const GENERATED_AT_PLUS_EIGHT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?\+08:00$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MAX_CONTENT_CODE_POINTS = 4000;

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: UnknownRecord, expectedKeys: readonly string[]): boolean {
  const actualKeys = Object.keys(value);
  return actualKeys.length === expectedKeys.length && expectedKeys.every(key => Object.prototype.hasOwnProperty.call(value, key));
}

function isGeneratedAt(value: unknown): value is string {
  return typeof value === 'string' && GENERATED_AT_PLUS_EIGHT.test(value) && DateTime.fromISO(value, { setZone: true }).isValid;
}

function isValidContent(value: unknown): value is string {
  return typeof value === 'string' && Array.from(value).length <= MAX_CONTENT_CODE_POINTS && isMarkdownV1(value);
}

function parseAnnouncement(value: unknown, seenIds: Set<string>): PortalAnnouncement | null {
  if (!isRecord(value) || !hasExactKeys(value, ['id', 'contentFormat', 'content'])) {
    return null;
  }

  const id = value['id'];
  const contentFormat = value['contentFormat'];
  const content = value['content'];
  if (
    typeof id !== 'string' ||
    !UUID.test(id) ||
    seenIds.has(id.toLowerCase()) ||
    contentFormat !== 'markdown-v1' ||
    !isRecord(content) ||
    !hasExactKeys(content, ['en', 'zhHant'])
  ) {
    return null;
  }

  const english = content['en'];
  const traditionalChinese = content['zhHant'];
  if (!isValidContent(english) || !isValidContent(traditionalChinese)) {
    return null;
  }

  seenIds.add(id.toLowerCase());
  return {
    id,
    contentFormat,
    content: {
      en: english,
      zhHant: traditionalChinese,
    },
  };
}

export function parseAnnouncementManifest(value: unknown): AnnouncementManifest | null {
  if (!isRecord(value) || !hasExactKeys(value, ['schemaVersion', 'generatedAt', 'announcements'])) {
    return null;
  }
  if (value['schemaVersion'] !== 1 || !isGeneratedAt(value['generatedAt']) || !Array.isArray(value['announcements']) || value['announcements'].length > 3) {
    return null;
  }

  const seenIds = new Set<string>();
  const announcements: PortalAnnouncement[] = [];
  for (const rawAnnouncement of value['announcements']) {
    const announcement = parseAnnouncement(rawAnnouncement, seenIds);
    if (!announcement) {
      return null;
    }
    announcements.push(announcement);
  }

  return {
    schemaVersion: 1,
    generatedAt: value['generatedAt'],
    announcements,
  };
}

@Injectable({
  providedIn: 'root',
})
export class ReleaseNotificationService {
  private http = new HttpClient(inject(HttpBackend));
  private announcements$ = concat(of(0), interval(POLL_INTERVAL_MS)).pipe(
    switchMap(() => this.loadAnnouncements()),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  getAnnouncements(): Observable<PortalAnnouncement[]> {
    return this.announcements$;
  }

  private loadAnnouncements(): Observable<PortalAnnouncement[]> {
    return this.http
      .get<unknown>(ANNOUNCEMENT_URL, {
        observe: 'response',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          'ngsw-bypass': 'true',
        },
      })
      .pipe(
        timeout(environment.httpTimeout),
        map(response => this.readResponse(response)),
        catchError(() => {
          console.warn('Announcement manifest could not be loaded; continuing without published announcements.');
          return of([]);
        })
      );
  }

  private readResponse(response: HttpResponse<unknown>): PortalAnnouncement[] {
    const contentType = response.headers.get('Content-Type');
    const manifest = JSON_CONTENT_TYPE.test(contentType || '') ? parseAnnouncementManifest(response.body) : null;
    if (!manifest) {
      throw new Error('Announcement manifest response is invalid.');
    }
    return manifest.announcements;
  }
}
